/**
 * CameraCommandsPanel — 15 camera command buttons in a 5x3 grid.
 *
 * Appears below RichTextBox when motion prompt edit mode is active.
 * Each button inserts camera command tags into the RichTextBox at the
 * cursor position or wraps the current selection.
 */

import { memo, useCallback } from 'react'
import type { RichTextBoxHandle } from './RichTextBox'

const CAMERA_COMMANDS = [
  // Row 1
  'Zoom In', 'Zoom Out', 'Pan Left', 'Pan Right', 'Tilt Up',
  // Row 2
  'Tilt Down', 'Push In', 'Pull Out', 'Truck Left', 'Truck Right',
  // Row 3
  'Pedestal Up', 'Pedestal Down', 'Shake', 'Tracking', 'Static',
] as const

interface CameraCommandsPanelProps {
  richTextBoxRef: React.RefObject<RichTextBoxHandle | null>
}

export const CameraCommandsPanel = memo(function CameraCommandsPanel({
  richTextBoxRef,
}: CameraCommandsPanelProps) {
  const handleCommandClick = useCallback(
    (command: string) => {
      const handle = richTextBoxRef.current
      if (!handle) return

      const openTag = `[${command}]`
      const closeTag = `[${command} End]`

      handle.wrapSelection(openTag, closeTag)
    },
    [richTextBoxRef],
  )

  return (
    <div data-testid="camera-commands-panel">
      {/* Label with info tooltip */}
      <div className="flex items-center gap-1 mb-1.5">
        <span className="text-xxs uppercase tracking-wider text-text-muted">
          Camera Commands
        </span>
        <span className="relative group/info">
          <svg
            className="w-3 h-3 text-text-muted/50 cursor-help"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="absolute bottom-full left-0 mb-1 px-2 py-1 rounded bg-black/90 text-[9px] text-white/80 whitespace-nowrap opacity-0 group-hover/info:opacity-100 transition-opacity duration-100 pointer-events-none z-50 shadow-lg">
            Click to insert camera direction tags into your prompt
          </span>
        </span>
      </div>

      {/* 5x3 grid */}
      <div
        className="grid gap-1"
        style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}
      >
        {CAMERA_COMMANDS.map((command) => (
          <button
            key={command}
            type="button"
            onMouseDown={(e) => {
              // Prevent stealing focus from RichTextBox
              e.preventDefault()
              handleCommandClick(command)
            }}
            className="px-1.5 py-1 text-[9px] rounded-full border border-border-subtle bg-surface-2 text-text-secondary hover:border-primary/40 hover:text-primary hover:bg-primary/10 transition-colors truncate"
            title={command}
            data-testid={`camera-cmd-${command.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {command}
          </button>
        ))}
      </div>
    </div>
  )
})

CameraCommandsPanel.displayName = 'CameraCommandsPanel'
